/**
 * BusinessFields — co-located field primitive for the Voice onboarding "Your
 * business" step (3/7). A labelled input with a leading inline icon, an optional
 * Smart hint line, and an inline validation message. Brand tokens only; focus
 * ring uses genie-primary. Lucide used for inline icons only.
 */
import type { LucideIcon } from "lucide-react";
import { Sparkles } from "lucide-react";
import { forwardRef } from "react";
import type { InputHTMLAttributes, ReactNode } from "react";

interface OnbFieldProps extends InputHTMLAttributes<HTMLInputElement> {
  icon: LucideIcon;
  label: ReactNode;
  /** Smart hint shown beneath the field */
  hint?: string;
  /** Inline validation message (overrides hint when present) */
  error?: string;
}

export const OnbField = forwardRef<HTMLInputElement, OnbFieldProps>(
  ({ icon: Icon, label, hint, error, className, ...rest }, ref) => {
    return (
      <div className="flex flex-col gap-1.5">
        <label className="text-[12.5px] font-semibold text-[#475569]">{label}</label>
        <div
          className={`group h-[50px] rounded-xl bg-white border flex flex-row items-center gap-2.5 px-3.5 transition-colors focus-within:ring-2 focus-within:ring-genie-primary/40 focus-within:border-genie-primary ${
            error ? "border-genie-secondary" : "border-gray-200"
          }`}
        >
          <Icon
            className="w-[18px] h-[18px] text-gray-400 flex-shrink-0 group-focus-within:text-genie-primary transition-colors"
            strokeWidth={1.8}
          />
          <input
            ref={ref}
            className={`flex-1 bg-transparent text-[15px] font-medium text-[#222B45] placeholder:font-normal placeholder:text-gray-400 outline-none ${className ?? ""}`}
            {...rest}
          />
        </div>
        {error ? (
          <p className="text-[11.5px] font-medium text-genie-secondary">{error}</p>
        ) : (
          hint && (
            <div className="flex flex-row items-center gap-1.5">
              <Sparkles className="w-3 h-3 text-genie-secondary flex-shrink-0" strokeWidth={1.8} />
              <p className="text-[11.5px] text-gray-500">{hint}</p>
            </div>
          )
        )}
      </div>
    );
  },
);

OnbField.displayName = "OnbField";

export default OnbField;
